import React, { useEffect, useState, Children, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ExternalLinkIcon, ChevronDownIcon } from 'lucide-react';
import { CTABanner } from './CTABanner';
import { CapabilityModal } from './CapabilityModal';
export interface Feature {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  detailedDescription?: string;
  image?: string;
}
export interface FAQ {
  question: string;
  answer: string;
}
interface SolutionPageLayoutProps {
  label: string;
  title: string;
  subtitle: string;
  features: Feature[];
  faqs?: FAQ[];
  /**
   * Optional link to the reference guide covering this problem space. Without
   * it, traffic only ever flows guides -> solutions and the solution pages
   * never feed the library.
   */
  referenceGuide?: {
    slug: string;
    title: string;
    question: string;
  };
}
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45
    }
  }
};
export function SolutionPageLayout({
  label,
  title,
  subtitle,
  features,
  faqs,
  referenceGuide
}: SolutionPageLayoutProps) {
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <>
      {/* Hero */}
      <section className="relative w-full pt-32 pb-20 px-6 overflow-hidden bg-navy" data-id="element-429" data-mp="wbpjv1my4tsg">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-430" data-mp="1137pi2zwzijr">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full animate-glow-pulse" style={{
          background: 'radial-gradient(ellipse at center, rgba(108, 92, 231, 0.2) 0%, rgba(200, 80, 192, 0.08) 40%, transparent 70%)'
        }} data-id="element-431" data-mp="3na8cp30wtlv" />

        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-432" data-mp="1buy21i846367">
          <motion.span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-433" data-mp="16xocm2kq5rid">

            {label}
          </motion.span>

          <motion.h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} data-id="element-434" data-mp="nzvb94pr1pn3">

            {title}
          </motion.h1>

          <motion.p className="text-lg text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} data-id="element-435" data-mp="2dgcvupolpzv8">

            {subtitle}
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.35
        }} data-id="element-436" data-mp="26y9r8d1ic711">

            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-437" data-mp="34ls86b5bofg4">

              Get a Demo
              <ArrowRightIcon className="w-4 h-4" data-id="element-438" data-mp="2utm6g9p9qjnq" />
            </a>
            <a href="https://app.thruline.co/non-auth/signup" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/25 text-white font-medium text-sm hover:border-white/50 transition-all" data-id="element-439" data-mp="3ikromviolo17">

              Sign up for free
            </a>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-navy-light to-transparent pointer-events-none" data-id="element-440" data-mp="1uo23sys792i8" />
      </section>

      {/* Features Grid */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-441" data-mp="1krh3gqr4kfkq">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-442" data-mp="2iefyyy62zhh1" />

        <div className="max-w-5xl mx-auto" data-id="element-443" data-mp="2dfv135eax9vr">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} data-id="element-444" data-mp="28ompq3tevefy">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-445" data-mp="2we7bsvkv2rtg">
              Capabilities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-446" data-mp="33qwtc085xdet">
              What's included
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-447" data-mp="3dn057xstgqey">

            {features.map(feature => {
            const Icon = feature.icon;
            return <motion.button key={feature.title} variants={itemVariants} onClick={() => setSelectedFeature(feature)} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-7 backdrop-blur-sm text-left cursor-pointer group hover:border-violet-electric/20 hover:bg-white/[0.05] transition-all duration-200" data-id="element-448" data-mp="3rmizbmosm189">

                  <div className="flex items-start justify-between mb-4" data-id="element-449" data-mp="a8dw3gwpuc41">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-electric/20 to-violet-electric/5 flex items-center justify-center" data-id="element-450" data-mp="e7mreq7emi1p">
                      <Icon className="w-5 h-5 text-violet-electric" data-id="element-451" data-mp="2b6o4dgydfgva" />
                    </div>
                    <ExternalLinkIcon className="w-4 h-4 text-white/0 group-hover:text-white/30 transition-all duration-200 mt-1" data-id="element-452" data-mp="2684i1ffv832e" />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2 group-hover:text-white transition-colors" data-id="element-453" data-mp="2f9ks1m2rq363">
                    {feature.title}
                  </h3>
                  <p className="text-white/45 text-sm leading-relaxed" data-id="element-454" data-mp="2p0yn9hnosgtf">
                    {feature.description}
                  </p>
                </motion.button>;
          })}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && <section className="relative w-full bg-navy py-24 px-6" data-id="element-455" data-mp="18m7846fjx622">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" data-id="element-456" data-mp="18mgw5oh1d354" />

          <div className="max-w-3xl mx-auto" data-id="element-457" data-mp="ug57z6n7k8e1">
            <motion.div className="text-center mb-14" initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} data-id="element-458" data-mp="uhgma0ea4yje">

              <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-459" data-mp="po3zlvt5v1ta">
                FAQ
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-460" data-mp="fsagftkxsa2j">
                Common questions
              </h2>
            </motion.div>

            <motion.div className="space-y-3" initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-461" data-mp="1ucejnnsy63x">

              {faqs.map((faq, index) => <div key={index} className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-462" data-mp="39pssfoxxum6t" data-mpi={index}>

                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-5 text-left cursor-pointer group" data-id="element-463" data-mp="34rr6mw94o8rn">

                    <span className="text-white font-medium text-sm pr-4 group-hover:text-white/90 transition-colors" data-id="element-464" data-mp="2vyxqkwlux5cq">
                      {faq.question}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 text-white/40 flex-shrink-0 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-465" data-mp="2m0856tur52rq" />

                  </button>
                  <AnimatePresence data-id="element-466" data-mp="2cza3ii7sd7cb">
                    {openFaq === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-467" data-mp="2d0uyg8y0isap">

                        <div className="px-5 pb-5 pt-0" data-id="element-468" data-mp="1y2sam0pbdvhf">
                          <p className="text-white/50 text-sm leading-relaxed" data-id="element-469" data-mp="22v3fzj2tq9hd">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </motion.div>
          </div>
        </section>}

      {/* Reference guide cross-link */}
      {referenceGuide && <section className="relative w-full bg-navy py-16 px-6" data-id="element-470" data-mp="1j6ukaz22nld4">
          <div className="max-w-3xl mx-auto" data-id="element-471" data-mp="1j9p5wtwtnc84">
            <Link to={`/resources/reference-guides/${referenceGuide.slug}`} className="group block rounded-2xl border border-white/[0.08] bg-white/[0.02] p-7 hover:border-violet-link/30 hover:bg-white/[0.04] transition-colors" data-id="element-472" data-mp="1o6pa0za424wl">

              <span className="text-violet-link text-xs font-semibold tracking-widest uppercase" data-id="element-473" data-mp="2zjrgv2merg3k">
                Reference Guide
              </span>
              <p className="text-white text-xl font-bold mt-2.5 mb-2 group-hover:text-violet-link transition-colors" data-id="element-474" data-mp="heyjknhphtrz">
                {referenceGuide.title}
              </p>
              <p className="text-white/60 text-base leading-relaxed mb-5" data-id="element-475" data-mp="m8kqdz16v3fv">
                {referenceGuide.question}
              </p>
              <span className="inline-flex items-center gap-2 text-violet-link text-sm font-medium group-hover:gap-3 transition-all" data-id="element-476" data-mp="3pnqgnuf4823r">
                Read the vendor-neutral guide
                <ArrowRightIcon aria-hidden="true" className="w-4 h-4" data-id="element-477" data-mp="3fyuxfu71evt" />
              </span>
            </Link>
          </div>
        </section>}

      {/* CTA */}
      <CTABanner data-id="element-478" data-mp="7in2zlb32hrq" />

      {/* Modal */}
      <CapabilityModal feature={selectedFeature} onClose={() => setSelectedFeature(null)} data-id="element-479" data-mp="1ev5mtdwiu5qa" />

    </>;
}